<script setup lang="ts">
const route = useRoute()
const { locale, localePath } = useAppI18n()
const { setBreadcrumb } = useLsBreadcrumb()

const INFO_WINDOW_LOCATION_ASSET =
  'https://resources1.interface003.com/web30/2026/06/11/c1e3e37f-9ec4-4e86-a4c1-7a5798d383f1.png'
/** 缺省状态色，和设计稿保持一致。 */
const DEFAULT_STATUS_COLOR = '#338066'

type SurveyListItemData = {
  sid: string
  code?: string
  title?: string
  cover?: string
  evaluate?: string
  evaluatecolor?: string
  country?: string
  ico?: string
  company?: string
  date?: string
  urlPath?: string
  isexhibition?: boolean
  exhibition?: {
    position?: string
  }
  wiki_timestamp?: number
  isvideo?: boolean
  vr?: boolean
}

/** 组件入参，单条实勘列表数据。 */
const props = defineProps<{
  item: SurveyListItemData
  index: number
}>()

/** 对外数据的安全兜底，避免模板直接读到 undefined。 */
const item = computed(() => props.item ?? { sid: '' })

/** 对齐 Figma 的日期展示，只保留年月日。 */
const displayDate = computed(() => formatTimestamp(item.value.wiki_timestamp))

/** 状态色优先取接口字段，缺失时回退到设计默认绿。 */
const statusColor = computed(() => item.value.evaluatecolor || DEFAULT_STATUS_COLOR)

/** 优先使用公司简称，避免标题过长时元信息再重复。 */
const companyName = computed(() => item.value.company || item.value.code || '')

/** 详情页唯一标识，优先使用接口下发的 urlPath。 */
const detailKey = computed(() => item.value.urlPath)

/** 按实勘类型分流详情路由名，展会实勘走 expo 扩展路由。 */
const detailRouteName = computed(() =>
  item.value?.isexhibition ? 'inspections-expo-exchange-slug' : 'inspections-exchange-slug',
)

/** 点击新开详情页前缓存当前列表页，供详情页还原第二级面包屑。 */
const lsBreadcrumb = () => {
  setBreadcrumb(detailKey.value, {
    labelKey: '82521', // 走翻译的值
    route: route.fullPath,
  })
}
</script>

<template>
  <NuxtLink
    :to="
      localePath({
        name: detailRouteName,
        params: {
          slug: detailKey,
        },
      })
    "
    target="_blank"
    class="block w-full border-b-[0.5px] border-[rgba(5,21,36,0.06)] py-[24px] hover:bg-[#F8F8F8]"
    @click="lsBreadcrumb"
  >
    <div class="flex w-full items-start gap-[20px]">
      <div class="relative flex h-[120px] w-[213px] shrink-0 overflow-hidden rounded-[8px] bg-[#f5f7fb]">
        <CommonImage
          class="size-full object-cover"
          :class="item.isvideo ? 'video-cover' : ''"
          :src="item.cover?.replace('default', 'survey-list-webp')"
          alt="cover"
          :loading="index <= 10 ? 'eager' : 'lazy'"
        />
        <div
          v-if="item?.vr"
          class="absolute bottom-[12px] left-[12px] flex h-[24px] w-[38px] overflow-hidden rtl:right-[12px] rtl:left-auto"
        >
          <CommonImage
            class="size-full object-cover"
            src="https://resources1.interface003.com/web30/2026/06/11/2a489d86-1e3b-4f45-a189-29af64a20e6b.gif"
            alt="vr"
            :loading="index <= 10 ? 'eager' : 'lazy'"
          />
        </div>
      </div>

      <div class="flex min-w-0 flex-1 flex-col gap-[12px]">
        <p class="line-clamp-2 h-[52px] text-[18px] leading-[26px] font-[600] text-[#292a2e]">
          {{ item.title }}
        </p>

        <div class="flex w-full items-center gap-[8px]">
          <div
            v-if="item?.isexhibition"
            class="flex h-[22px] shrink-0 items-center rounded-[2px] bg-[#185CA0] px-[8px] py-[2px] text-[12px] leading-[18px] text-white"
          >
            EXPO
          </div>
          <div
            v-if="item.evaluate"
            class="flex h-[22px] shrink-0 items-center rounded-[2px] px-[8px] py-[2px]"
            :style="{
              backgroundColor: statusColor,
            }"
          >
            <span class="text-[12px] leading-[18px] font-[400] whitespace-nowrap text-white">
              {{ item.evaluate }}
            </span>
          </div>

          <div v-if="item.country" class="flex min-w-0 items-center gap-[4px]">
            <div class="flex size-[20px] shrink-0 overflow-hidden">
              <CommonImage
                :src="INFO_WINDOW_LOCATION_ASSET"
                alt="location icon"
                class="size-full object-cover"
                :loading="index <= 10 ? 'eager' : 'lazy'"
              />
            </div>
            <span class="truncate text-[14px] leading-[22px] font-[400] text-[#7d818a]">
              {{ item.country }}
            </span>
          </div>
        </div>

        <div class="flex w-full items-center gap-[8px]">
          <div class="flex min-w-0 flex-1 items-center gap-[4px]">
            <div class="flex size-[20px] shrink-0 overflow-hidden rounded-[2px] bg-[#f5f7fb] leading-[20px]">
              <CommonImage
                :src="item.ico"
                alt="icon"
                class="size-full object-cover"
                :loading="index <= 10 ? 'eager' : 'lazy'"
              />
            </div>
            <span class="truncate text-[14px] leading-[22px] font-[400] text-[#7d818a]">
              {{ companyName }}
            </span>

            <span
              v-if="item?.isexhibition && item?.exhibition?.position"
              class="mx-[8px] h-[12px] w-[1px] bg-[rgba(5,21,36,0.06)]"
            ></span>

            <span
              v-if="item?.isexhibition && item?.exhibition?.position"
              class="truncate text-[14px] leading-[22px] font-[400] text-[#7d818a]"
              >{{ item.exhibition.position }}</span
            >
          </div>

          <span v-if="displayDate" dir="ltr" class="ftf shrink-0 text-[14px] leading-[22px] font-[400] text-[#7d818a]">
            {{ displayDate }}
          </span>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped></style>
